import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAuth } from '../../store/authContext';
import {
  getRiskActivity,
  getRiskEvent,
  getRiskEvents,
  getRiskRules,
  getRiskSummary,
  updateRiskEventStatus,
  updateRiskRule,
} from '../../api/risk';
import {
  formatMetric,
  formatWindow,
  riskCategoryMeta,
  riskRuleValidationError,
  riskSeverityMeta,
  riskStatusMeta,
} from './riskPresentation';
import './style.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const TAIPEI_ZONE = 'Asia/Taipei';

const exactTime = (value) => (value
  ? dayjs.utc(value).tz(TAIPEI_ZONE).format('YYYY-MM-DD HH:mm:ss')
  : '-');

const metaTag = (value, source) => {
  const meta = source[value] || { label: value || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const RiskControl = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEditRules = admin?.role === 'SUPER_ADMIN';
  const [activeTab, setActiveTab] = useState('events');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventPage, setEventPage] = useState({ current: 1, pageSize: 20, total: 0 });
  const initialFilters = useMemo(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const timeRange = from && to && dayjs(from).isValid() && dayjs(to).isValid()
      ? [dayjs(from), dayjs(to)] : undefined;
    return {
      userId: searchParams.get('userId') || '',
      category: searchParams.get('category') || '',
      ruleCode: searchParams.get('ruleCode') || '',
      severity: searchParams.get('severity') || '',
      status: searchParams.has('status') ? searchParams.get('status') : 'OPEN',
      timeRange,
    };
  }, [searchParams]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [handleOpen, setHandleOpen] = useState(false);
  const [handleLoading, setHandleLoading] = useState(false);
  const [handleForm] = Form.useForm();
  const [activityLoading, setActivityLoading] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityUserId, setActivityUserId] = useState('');
  const [appliedActivityUserId, setAppliedActivityUserId] = useState('');
  const [activityPage, setActivityPage] = useState({ current: 1, pageSize: 20, total: 0 });
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm] = Form.useForm();
  const eventPageCurrent = eventPage.current;
  const eventPageSize = eventPage.pageSize;
  const activityPageCurrent = activityPage.current;
  const activityPageSize = activityPage.pageSize;

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await getRiskSummary());
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setEventLoading(true);
    try {
      const range = appliedFilters.timeRange;
      const result = await getRiskEvents({
        page: eventPageCurrent,
        size: eventPageSize,
        userId: appliedFilters.userId || undefined,
        category: appliedFilters.category || undefined,
        ruleCode: appliedFilters.ruleCode || undefined,
        severity: appliedFilters.severity || undefined,
        status: appliedFilters.status || undefined,
        from: range?.[0]?.toISOString(),
        to: range?.[1]?.toISOString(),
      });
      setEvents(result.list || []);
      setEventPage((previous) => ({ ...previous, total: result.total || 0 }));
    } finally {
      setEventLoading(false);
    }
  }, [appliedFilters, eventPageCurrent, eventPageSize]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const result = await getRiskActivity({
        page: activityPageCurrent,
        size: activityPageSize,
        userId: appliedActivityUserId || undefined,
      });
      setActivity(result.list || []);
      setActivityPage((previous) => ({ ...previous, total: result.total || 0 }));
    } finally {
      setActivityLoading(false);
    }
  }, [activityPageCurrent, activityPageSize, appliedActivityUserId]);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      setRules(await getRiskRules());
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (activeTab === 'events') loadEvents(); }, [activeTab, loadEvents]);
  useEffect(() => { if (activeTab === 'activity') loadActivity(); }, [activeTab, loadActivity]);
  useEffect(() => { if (activeTab === 'rules') loadRules(); }, [activeTab, loadRules]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      setDetail(await getRiskEvent(id));
    } finally {
      setDetailLoading(false);
    }
  };

  const applyFilters = () => {
    if (filters.userId && !/^[1-9]\d*$/.test(String(filters.userId))) {
      message.error('用户 ID 必须是正整数');
      return;
    }
    setAppliedFilters({ ...filters });
    setEventPage((previous) => ({ ...previous, current: 1 }));
    const params = {};
    ['userId', 'category', 'ruleCode', 'severity', 'status'].forEach((key) => {
      if (filters[key]) params[key] = filters[key];
    });
    if (filters.timeRange?.[0] && filters.timeRange?.[1]) {
      params.from = filters.timeRange[0].toISOString();
      params.to = filters.timeRange[1].toISOString();
    }
    setSearchParams(params);
  };

  const showUserTimeline = (userId) => {
    const next = { status: '', userId: String(userId) };
    setFilters(next);
    setAppliedFilters(next);
    setSearchParams({ userId: String(userId) });
    setEventPage((previous) => ({ ...previous, current: 1 }));
    setDetail(null);
    setActiveTab('events');
  };

  const queryActivity = () => {
    if (activityUserId && !/^[1-9]\d*$/.test(String(activityUserId))) {
      message.error('用户 ID 必须是正整数');
      return;
    }
    const shouldReload = activityPageCurrent === 1
      && String(appliedActivityUserId) === String(activityUserId);
    setActivityPage((previous) => ({ ...previous, current: 1 }));
    setAppliedActivityUserId(activityUserId);
    if (shouldReload) loadActivity();
  };

  const submitHandle = async () => {
    const values = await handleForm.validateFields();
    if (values.status === 'IGNORED' && !values.note?.trim()) {
      handleForm.setFields([{ name: 'note', errors: ['忽略事件必须填写原因'] }]);
      return;
    }
    setHandleLoading(true);
    try {
      await updateRiskEventStatus(detail.id, {
        status: values.status,
        note: values.note?.trim() || undefined,
        version: detail.version,
      });
      message.success(values.status === 'CONFIRMED' ? '风险事件已确认' : '风险事件已忽略');
      setHandleOpen(false);
      setDetail(null);
      await Promise.all([loadEvents(), loadSummary()]);
    } catch (error) {
      if (error?.code === 409 || error?.kind === 'conflict') {
        message.info('事件已被其他管理员处理，列表已刷新');
        setHandleOpen(false);
        setDetail(null);
        loadEvents();
      }
    } finally {
      setHandleLoading(false);
    }
  };

  const openRuleEditor = (rule) => {
    setEditingRule(rule);
    ruleForm.setFieldsValue({
      enabled: rule.enabled,
      windowMinutes: rule.windowMinutes,
      mediumThreshold: Number(rule.mediumThreshold),
      highThreshold: Number(rule.highThreshold),
      minimumSampleSize: rule.minimumSampleSize,
    });
    setRuleModalOpen(true);
  };

  const saveRule = async () => {
    const values = await ruleForm.validateFields();
    const validationError = riskRuleValidationError(values);
    if (validationError) {
      message.error(validationError);
      return;
    }
    setRuleSaving(true);
    try {
      await updateRiskRule(editingRule.ruleCode, { ...values, version: editingRule.version });
      message.success('风控规则已更新');
      setRuleModalOpen(false);
      await loadRules();
    } catch (error) {
      if (error?.code === 409 || error?.kind === 'conflict') {
        message.info('规则版本已变化，已加载最新配置');
        setRuleModalOpen(false);
        loadRules();
      }
    } finally {
      setRuleSaving(false);
    }
  };

  const eventColumns = [
    { title: '事件', dataIndex: 'summary', render: (value, row) => (
      <Space direction="vertical" size={0}>
        <Text strong>{value}</Text>
        <Text type="secondary">{row.ruleCode}</Text>
      </Space>
    ) },
    { title: '分类', dataIndex: 'category', width: 110, render: (value) => metaTag(value, riskCategoryMeta) },
    { title: '等级', dataIndex: 'severity', width: 90, render: (value) => metaTag(value, riskSeverityMeta) },
    { title: '状态', dataIndex: 'status', width: 90, render: (value) => metaTag(value, riskStatusMeta) },
    { title: '指标 / 阈值', key: 'metric', width: 150, render: (_, row) => (
      <Space direction="vertical" size={0}>
        <Text strong>{formatMetric(row.metricValue, row.metricUnit)}</Text>
        <Text type="secondary">阈值 {formatMetric(row.thresholdValue, row.metricUnit)}</Text>
      </Space>
    ) },
    { title: '主用户', key: 'user', width: 150, render: (_, row) => row.primaryUser ? (
      <Space direction="vertical" size={0}>
        <Button type="link" className="risk-link-button" onClick={() => showUserTimeline(row.primaryUser.userId)}>
          {row.primaryUser.nickname || `用户 ${row.primaryUser.userId}`}
        </Button>
        <Text type="secondary">ID {row.primaryUser.userId}{row.relatedUserCount ? ` · 关联 ${row.relatedUserCount} 人` : ''}</Text>
      </Space>
    ) : '-' },
    { title: '最近发生时间', dataIndex: 'lastOccurredAt', width: 178, render: exactTime },
    { title: '次数', dataIndex: 'occurrenceCount', width: 72 },
    { title: '操作', key: 'actions', fixed: 'right', width: 86, render: (_, row) => (
      <Tooltip title="查看事件详情">
        <Button icon={<EyeOutlined />} onClick={() => openDetail(row.id)} aria-label="查看事件详情" />
      </Tooltip>
    ) },
  ];

  const activityColumns = [
    { title: '用户', key: 'user', render: (_, row) => (
      <Button type="link" className="risk-link-button" onClick={() => navigate(`/admin/users?userId=${row.userId}`)}>
        {row.nickname || `用户 ${row.userId}`} · {row.maskedPhone || '无手机号'}
      </Button>
    ) },
    { title: '会员', dataIndex: 'membershipType', width: 120, render: (value) => <Tag color={value === 'FREE' ? 'default' : 'gold'}>{value}</Tag> },
    { title: '今日询问', dataIndex: 'todayCount', width: 120 },
    { title: '本周询问', dataIndex: 'periodCount', width: 120 },
    { title: '最近询问时间', dataIndex: 'latestAt', width: 178, render: exactTime },
  ];

  const ruleColumns = [
    { title: '规则', key: 'rule', render: (_, row) => (
      <Space direction="vertical" size={0}><Text strong>{row.displayName}</Text><Text type="secondary">{row.ruleCode}</Text></Space>
    ) },
    { title: '分类', dataIndex: 'category', width: 110, render: (value) => metaTag(value, riskCategoryMeta) },
    { title: '状态', dataIndex: 'enabled', width: 80, render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? '启用' : '停用'}</Tag> },
    { title: '统计窗口', dataIndex: 'windowMinutes', width: 110, render: formatWindow },
    { title: '中风险阈值', key: 'medium', width: 130, render: (_, row) => formatMetric(row.mediumThreshold, row.metricUnit) },
    { title: '高风险阈值', key: 'high', width: 130, render: (_, row) => formatMetric(row.highThreshold, row.metricUnit) },
    { title: '最小样本', dataIndex: 'minimumSampleSize', width: 100 },
    { title: '版本', dataIndex: 'version', width: 72 },
    ...(canEditRules ? [{ title: '操作', width: 72, render: (_, row) => (
      <Tooltip title="编辑规则"><Button icon={<EditOutlined />} onClick={() => openRuleEditor(row)} aria-label="编辑规则" /></Tooltip>
    ) }] : []),
  ];

  const summaryItems = [
    ['待处理事件', summary?.openEventCount || 0],
    ['今日新增', summary?.todayNewEventCount || 0],
    ['高风险用户', summary?.openHighRiskUserCount || 0],
    ['AI 待处理', summary?.openAiEventCount || 0],
  ];

  return (
    <div className="risk-page">
      <div className="risk-page-header">
        <Title level={2}>风控中心</Title>
        <Tooltip title="刷新概览和当前列表">
          <Button
            icon={<ReloadOutlined />}
            loading={summaryLoading}
            onClick={() => {
              loadSummary();
              if (activeTab === 'events') loadEvents();
              if (activeTab === 'activity') loadActivity();
              if (activeTab === 'rules') loadRules();
            }}
            aria-label="刷新"
          />
        </Tooltip>
      </div>

      <div className="risk-summary" aria-label="风险概览">
        {summaryItems.map(([label, value]) => (
          <Statistic key={label} title={label} value={value} loading={summaryLoading} />
        ))}
        <div className="risk-category-counts">
          {Object.entries(riskCategoryMeta).map(([key, meta]) => (
            <Tag key={key} color={meta.color}>{meta.label} {summary?.openCategoryCounts?.[key] || 0}</Tag>
          ))}
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'events', label: '风险事件', children: (
          <>
            <div className="risk-filter-bar">
              <Input placeholder="用户 ID" value={filters.userId} onChange={(event) => setFilters({ ...filters, userId: event.target.value })} />
              <Select allowClear placeholder="风险分类" value={filters.category || undefined} onChange={(value) => setFilters({ ...filters, category: value })} options={Object.entries(riskCategoryMeta).map(([value, meta]) => ({ value, label: meta.label }))} />
              <Input placeholder="规则代码" value={filters.ruleCode} onChange={(event) => setFilters({ ...filters, ruleCode: event.target.value })} />
              <Select allowClear placeholder="风险等级" value={filters.severity || undefined} onChange={(value) => setFilters({ ...filters, severity: value })} options={Object.entries(riskSeverityMeta).map(([value, meta]) => ({ value, label: meta.label }))} />
              <Select allowClear placeholder="处理状态" value={filters.status || undefined} onChange={(value) => setFilters({ ...filters, status: value })} options={Object.entries(riskStatusMeta).map(([value, meta]) => ({ value, label: meta.label }))} />
              <RangePicker showTime value={filters.timeRange} onChange={(value) => setFilters({ ...filters, timeRange: value })} />
              <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
            </div>
            <Table rowKey="id" columns={eventColumns} dataSource={events} loading={eventLoading} scroll={{ x: 1180 }} pagination={{ ...eventPage, showSizeChanger: true, showTotal: (total) => `共 ${total} 条`, onChange: (current, pageSize) => setEventPage({ current, pageSize, total: eventPage.total }) }} />
          </>
        ) },
        { key: 'activity', label: 'AI 使用统计', children: (
          <>
            <div className="risk-filter-bar risk-filter-bar-compact">
              <Input placeholder="用户 ID" value={activityUserId} onChange={(event) => setActivityUserId(event.target.value)} onPressEnter={queryActivity} />
              <Button type="primary" icon={<SearchOutlined />} onClick={queryActivity}>查询</Button>
            </div>
            <Table rowKey="userId" columns={activityColumns} dataSource={activity} loading={activityLoading} pagination={{ ...activityPage, showSizeChanger: true, showTotal: (total) => `共 ${total} 个用户`, onChange: (current, pageSize) => setActivityPage({ current, pageSize, total: activityPage.total }) }} />
          </>
        ) },
        { key: 'rules', label: '规则配置', children: (
          <>
            {!canEditRules && <Alert type="info" showIcon message="当前账号可查看规则，修改仅限超级管理员" className="risk-inline-alert" />}
            <Table rowKey="ruleCode" columns={ruleColumns} dataSource={rules} loading={rulesLoading} scroll={{ x: 980 }} pagination={false} />
          </>
        ) },
      ]} />

      <Drawer title="风险事件详情" width="min(680px, 100vw)" open={Boolean(detail)} onClose={() => setDetail(null)} loading={detailLoading} extra={detail?.status === 'OPEN' && (
        <Button type="primary" onClick={() => { handleForm.setFieldsValue({ status: 'CONFIRMED', note: '' }); setHandleOpen(true); }}>处理事件</Button>
      )}>
        {detail && !detailLoading ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <div><Title level={4}>{detail.summary}</Title><Space>{metaTag(detail.category, riskCategoryMeta)}{metaTag(detail.severity, riskSeverityMeta)}{metaTag(detail.status, riskStatusMeta)}</Space></div>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="规则">{detail.ruleCode}</Descriptions.Item>
              <Descriptions.Item label="规则版本">v{detail.ruleVersion}</Descriptions.Item>
              <Descriptions.Item label="指标">{formatMetric(detail.metricValue, detail.metricUnit)}</Descriptions.Item>
              <Descriptions.Item label="触发阈值">{formatMetric(detail.thresholdValue, detail.metricUnit)}</Descriptions.Item>
              <Descriptions.Item label="样本数">{detail.sampleSize}</Descriptions.Item>
              <Descriptions.Item label="累计命中">{detail.occurrenceCount}</Descriptions.Item>
              <Descriptions.Item label="首次发生">{exactTime(detail.firstOccurredAt)}</Descriptions.Item>
              <Descriptions.Item label="最近发生">{exactTime(detail.lastOccurredAt)}</Descriptions.Item>
              <Descriptions.Item label="统计窗口" span={2}>{exactTime(detail.windowStart)} 至 {exactTime(detail.windowEnd)}</Descriptions.Item>
              <Descriptions.Item label="来源" span={2}>{detail.sourceType || '-'}{detail.sourceId ? ` / ${detail.sourceId}` : ''}</Descriptions.Item>
              <Descriptions.Item label="关联标识">{detail.maskedSubjectHint || '-'}</Descriptions.Item>
              <Descriptions.Item label="证据快照"><Text code className="risk-evidence-text">{JSON.stringify(detail.evidence || {})}</Text></Descriptions.Item>
            </Descriptions>
            <div><Title level={5}>关联用户</Title>{detail.users?.length ? <Table size="small" rowKey="userId" pagination={false} dataSource={detail.users} columns={[
              { title: '关系', dataIndex: 'relationRole', render: (value) => value === 'PRIMARY' ? '主用户' : '关联用户' },
              { title: '用户', render: (_, row) => <Button type="link" className="risk-link-button" onClick={() => navigate(`/admin/users?userId=${row.userId}`)}>{row.nickname || `用户 ${row.userId}`} · {row.maskedPhone || '-'}</Button> },
              { title: '风险 / 黑名单', render: (_, row) => `${row.riskLevel || '-'} / ${row.blacklisted ? '是' : '否'}` },
              { title: '操作', render: (_, row) => <Button size="small" onClick={() => showUserTimeline(row.userId)}>时间线</Button> },
            ]} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}</div>
            {detail.actions?.length > 0 && <div><Title level={5}>处理记录</Title><Table size="small" rowKey={(row) => `${row.adminId}-${row.actionAt}`} pagination={false} dataSource={detail.actions} columns={[
              { title: '管理员', dataIndex: 'adminId' },
              { title: '状态', render: (_, row) => `${riskStatusMeta[row.previousStatus]?.label || row.previousStatus} → ${riskStatusMeta[row.newStatus]?.label || row.newStatus}` },
              { title: '备注', dataIndex: 'note', render: (value) => value || '-' },
              { title: '时间', dataIndex: 'actionAt', render: exactTime },
            ]} /></div>}
          </Space>
        ) : null}
      </Drawer>

      <Modal title="处理风险事件" open={handleOpen} onCancel={() => setHandleOpen(false)} onOk={submitHandle} confirmLoading={handleLoading}>
        <Form form={handleForm} layout="vertical">
          <Form.Item name="status" label="处理结果" rules={[{ required: true }]}>
            <Radio.Group optionType="button" buttonStyle="solid">
              <Radio.Button value="CONFIRMED"><CheckCircleOutlined /> 确认风险</Radio.Button>
              <Radio.Button value="IGNORED"><StopOutlined /> 忽略</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item shouldUpdate noStyle>{({ getFieldValue }) => <Form.Item name="note" label="处理备注" rules={getFieldValue('status') === 'IGNORED' ? [{ required: true, whitespace: true, message: '忽略事件必须填写原因' }] : []}><Input.TextArea rows={4} maxLength={500} showCount /></Form.Item>}</Form.Item>
        </Form>
      </Modal>

      <Modal title={editingRule?.displayName || '编辑规则'} open={ruleModalOpen} onCancel={() => setRuleModalOpen(false)} onOk={saveRule} confirmLoading={ruleSaving}>
        <Form form={ruleForm} layout="vertical">
          <Form.Item name="enabled" label="规则状态" valuePropName="checked"><Switch checkedChildren="启用" unCheckedChildren="停用" /></Form.Item>
          <Form.Item name="windowMinutes" label="统计窗口（分钟）" rules={[{ required: true }]}><InputNumber min={1} max={525600} precision={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="mediumThreshold" label="中风险阈值" rules={[{ required: true }]}><InputNumber min={0} precision={4} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="highThreshold" label="高风险阈值" rules={[{ required: true }]}><InputNumber min={0} precision={4} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="minimumSampleSize" label="最小样本数" rules={[{ required: true }]}><InputNumber min={1} max={1000000} precision={0} style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RiskControl;
